import React, { useState } from "react";
import withModal from "../withModal";
import { useSelector, useDispatch } from "react-redux";
import { hideModal, saveModelBundle } from "../../../actions/actionCreators";
import DualListBox from "../../listbox/DualListBox";
import defaultLang from "../../listbox/lang/default";

import MaterialModal from "../MaterialModal";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { IAppState } from "../../../types/stores/index";

type Props = {
    isOpen: boolean;
};

const ModelBundleCreateModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    const [excludedVariables, setExcludeVariables] = useState<Array<string>>([]);
    const [name, setName] = useState("");
    const [isSetGlobal, setIsSetGlobal] = useState(false);
    const [bundleId, setBundleId] = useState(1);
    const [errors, setErrors] = useState<{ name?: string; excluded?: string }>({});

    const modelBundles = useSelector((state: IAppState) => state.vars.modelBundles || []);
    const modelVariables = useSelector((state: IAppState) => state.vars.modelVariables || []);
    const modelExcludedVariables = useSelector((state: IAppState) => state.vars.modelExcludedVariables || []);
    const adminRoles = useSelector((state: IAppState) => state.session.roles || []);

    const hide = () => {
        dispatch(hideModal("MODEL_BUNDLE_CREATE"));
        setBundleId(1);
        setExcludeVariables([]);
        setName("");
        setErrors({});
        setIsSetGlobal(false);
    };

    const save = () => {
        if (!validate()) {
            return;
        }

        dispatch(saveModelBundle(name, excludedVariables, isSetGlobal));
        hide();
    };

    const validate = () => {
        let errorsValidated: { name?: string; excluded?: string } = {};
        const regex = /^$|^[-a-z0-9_() ]+$/i;

        // Check for blank name
        if (name.trim() == "") {
            errorsValidated.name = " Required";
        }
        if (!name.match(regex)) {
            errorsValidated.name = " Invalid characters";
        }

        if (modelBundles.some(d => d.label.toUpperCase().trim() == name.toUpperCase().trim())) {
            errorsValidated.name = " Bundle name is already used";
        }

        if (excludedVariables.length == 0) {
            errorsValidated.excluded = "Select at least 1 excluded variable";
        }
        setErrors(errorsValidated);
        return Object.keys(errorsValidated).length == 0;
    };

    const onNameChange = nameValue => {
        if (nameValue.length > 0) {
            let newErrors = errors;
            setErrors({ ...newErrors });
            setName("");
        }
        setName(nameValue);
    };

    const onVariablesChange = excludedVariablesValue => {
        if (excludedVariablesValue.length > 0) {
            let newErrors = errors;
            setErrors(newErrors);
            setExcludeVariables([]);
        }
        setExcludeVariables(excludedVariablesValue);
    };

    const onBundleChange = bundleIdValue => {
        const excludedVariablesSelected = modelExcludedVariables
            .filter(item => item.BundleId == bundleIdValue)
            .map(item => item.VariableName);
        setBundleId(bundleIdValue);
        setExcludeVariables(excludedVariablesSelected);
    };

    const renderName = () => {
        const isShowError = errors.name ? true : false;
        let nameTextBox = (
            <div className="m-t form-control-material">
                <TextField
                    fullWidth
                    error={isShowError}
                    label="Save Bundle As"
                    type="text"
                    value={name}
                    helperText={errors.name ? errors.name : " "}
                    onChange={e => onNameChange(e.target.value)}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
            </div>
        );

        return nameTextBox;
    };

    const renderBundle = () => {
        const options = modelBundles.map(company => (
            <MenuItem key={company.value} className="company-select-option" value={company.value}>
                {company.label}
            </MenuItem>
        ));

        let bundleIdNew = bundleId;
        if (bundleId == 0 && modelBundles.length > 0) {
            bundleIdNew = modelBundles[0].value;
        }

        let bundleSelect = (
            <FormControl
                style={{
                    flexGrow: 2,
                    marginBottom: 20,
                    fontSize: 14,
                    minWidth: "400px",
                    maxWidth: "100%",
                }}
                className="form-control-material"
            >
                <InputLabel id="bundle-select-label" htmlFor={`BundleSelect`}>
                    Start From Bundle
                </InputLabel>
                <Select
                    value={bundleIdNew}
                    fullWidth
                    onChange={e => {
                        onBundleChange(e.target.value);
                    }}
                >
                    {options}
                </Select>
            </FormControl>
        );

        return bundleSelect;
    };

    const renderVariables = () => {
        const options = modelVariables;

        let lang = defaultLang;
        let errorClassName = "has-error has-error-material";
        let errorMessageClassName = "help-block help-block-right";
        lang.availableHeader = "Included";
        lang.selectedHeader = "Excluded";

        return (
            <div className="row m-t">
                <div
                    className={
                        "col-sm-16 text-nowrap " +
                        (errors != null && Object.keys(errors).length != 0 ? errors.excluded && errorClassName : "")
                    }
                >
                    <DualListBox
                        options={options}
                        canFilter
                        selected={excludedVariables}
                        onChange={onVariablesChange}
                        showHeaderLabels={true}
                        lang={lang}
                    />
                    <div className={errorMessageClassName}>{errors.excluded ? errors.excluded : ""}</div>
                </div>
            </div>
        );
    };

    const renderGlobalCheck = () => (
        <FormControlLabel
            labelPlacement="start"
            label={<strong style={{ fontSize: "14px" }}>Save for All Companies</strong>}
            control={
                <Checkbox
                    id="is-set-global"
                    color="primary"
                    onChange={() => updateGlobalCheck(!isSetGlobal)}
                    value={isSetGlobal}
                    checked={isSetGlobal}
                />
            }
        />
    );

    const updateGlobalCheck = val => setIsSetGlobal(val);

    const renderModal = () => {
        const title = "Save Bundle";
        let nameTextBox = renderName();
        let variablesListBox = renderVariables();
        let bundleDropBox = renderBundle();
        const hasAdminRoleToSetGlobal = adminRoles.includes("Super Admin");
        const globalSection = hasAdminRoleToSetGlobal && renderGlobalCheck();
        let modal = (
            <MaterialModal
                isOpen={isOpen}
                title={title}
                showPrimaryButton={true}
                primaryButtonText="Save"
                onClose={hide}
                onPrimaryButtonRequest={save}
                id="bundle-create-modal"
            >
                <div className="flow-edit-body">{bundleDropBox}</div>
                <div>{nameTextBox}</div>
                {globalSection}
                <div>{variablesListBox}</div>
            </MaterialModal>
        );

        return modal;
    };

    return renderModal();
};

export default withModal(ModelBundleCreateModal, "MODEL_BUNDLE_CREATE");
